import { Box, Input as ChakraInput, forwardRef } from "@chakra-ui/react";
import { TbSearch, TbX } from "react-icons/tb";

/**
 * SearchInput — `.search` from the redesign bundle.
 * design ref: styles.css:878–929
 *
 * Container + leading search icon + clear button (only shown when there's a
 * value). `onChange` is invoked with the next string (not the event object) —
 * shape mirrors how the design's JSX consumers use it.
 *
 * Note: `.search input` uses `neutral.200` border (softer than `.input`'s
 * `neutral.300`); we honour that here.
 */
export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
  isDisabled?: boolean;
  autoFocus?: boolean;
  name?: string;
  id?: string;
  "aria-label"?: string;
}

const SearchInput = forwardRef<SearchInputProps, "input">(
  (
    {
      value,
      onChange,
      onClear,
      placeholder,
      isDisabled,
      autoFocus,
      name,
      id,
      // `size` (HTMLInputElement.size: number) collides with Chakra `Input`'s
      // `size: "sm" | "md" | …` prop; drop it before spreading rest.
      size: _size,
      ...rest
    },
    ref,
  ) => {
    const handleClear = () => {
      if (onClear) onClear();
      else onChange("");
    };

    const hasValue = value.length > 0;

    return (
      <Box position="relative" display="flex" alignItems="center" width="100%">
        {/* Leading search icon — absolute, left 12px. styles.css:906-911. */}
        <Box
          position="absolute"
          left="md" // 12px
          top="50%"
          transform="translateY(-50%)"
          color="neutral.600"
          pointerEvents="none"
          display="inline-flex"
          alignItems="center"
          aria-hidden
          zIndex={1}
        >
          <TbSearch size={16} />
        </Box>

        <ChakraInput
          ref={ref}
          variant="unstyled"
          width="100%"
          display="block"
          pl="36px"
          pr="36px"
          py="sm" // 8px
          fontSize="md"
          fontFamily="inherit"
          color="neutral.950"
          lineHeight="1.4"
          bg="neutral.25"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="neutral.200" // softer than .input — matches `.search input` border
          borderRadius="component.lg"
          boxShadow="xs"
          transition="border-color 120ms, box-shadow 120ms, background-color 120ms"
          _placeholder={{ color: "neutral.500" }}
          _hover={{
            borderColor: "neutral.400",
            bg: "neutral.0",
          }}
          _focus={{
            borderColor: "p.500",
            boxShadow: "focusRing",
            outline: "none",
          }}
          _focusVisible={{
            borderColor: "p.500",
            boxShadow: "focusRing",
            outline: "none",
          }}
          _disabled={{
            bg: "neutral.100",
            color: "neutral.500",
            borderColor: "neutral.200",
            cursor: "not-allowed",
          }}
          value={value}
          placeholder={placeholder}
          isDisabled={isDisabled}
          autoFocus={autoFocus}
          name={name}
          id={id}
          onChange={(e) => onChange(e.target.value)}
          {...rest}
        />

        {hasValue && (
          <Box
            as="button"
            type="button"
            aria-label="Clear search"
            onClick={handleClear}
            position="absolute"
            right="sm" // 8px
            top="50%"
            transform="translateY(-50%)"
            width="22px"
            height="22px"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            bg="transparent"
            border="none"
            borderRadius="component.md" // 6px
            color="neutral.600"
            cursor="pointer"
            transition="background-color 120ms, color 120ms"
            _hover={{
              bg: "neutral.100",
              color: "neutral.900",
            }}
          >
            <TbX size={14} />
          </Box>
        )}
      </Box>
    );
  },
);

export default SearchInput;
